/**
 * Analytics Service — Huqup Cafe & Grill
 *
 * Centralised wrapper for Google Analytics (gtag.js) and Microsoft Clarity.
 * Provides page-view tracking, custom event tracking, and Clarity tagging
 * through a single API consumed by React components.
 *
 * GA Measurement ID : G-GBZ2RKBQSP
 * Clarity Project ID: voougkoz7j
 */

const GA_ID = 'G-GBZ2RKBQSP';

/* ──────────── helpers ──────────── */

const gtag = (...args) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

const clarity = (...args) => {
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity(...args);
  }
};

/* ──────────── Page views ──────────── */

/**
 * Track a virtual page view (SPA route change).
 * Called automatically by the usePageTracking hook.
 */
const trackPageView = (path, title) => {
  gtag('config', GA_ID, {
    page_path: path,
    page_title: title || document.title,
  });
  // Clarity picks up URL changes automatically via its mutation observer,
  // but we tag the page for segmentation.
  clarity('set', 'page', path);
};

/* ──────────── Custom events ──────────── */

/**
 * Fire a GA4 event + mirror it as a Clarity custom tag.
 *
 * @param {string} eventName  GA4 event name (snake_case recommended)
 * @param {object} params     Optional event parameters
 */
const trackEvent = (eventName, params = {}) => {
  gtag('event', eventName, params);
  // Clarity custom tags (key, value) — flatten the first param for segmentation
  const label = params.event_label || params.label || '';
  if (label) {
    clarity('set', eventName, label);
  }
};

/* ──────────── Pre-built event helpers ──────────── */

/** User clicks a phone / call link */
const trackPhoneClick = (location = 'unknown') => {
  trackEvent('phone_click', {
    event_category: 'engagement',
    event_label: location,
  });
};

/** User clicks "Book a Table" or reservation CTA */
const trackReservationClick = (location = 'unknown') => {
  trackEvent('reservation_click', {
    event_category: 'conversion',
    event_label: location,
  });
};

/** User views the menu page or switches menu tabs */
const trackMenuView = (tab = 'all') => {
  trackEvent('menu_view', {
    event_category: 'engagement',
    event_label: tab,
  });
};

/** User clicks a social media link */
const trackSocialClick = (platform) => {
  trackEvent('social_click', {
    event_category: 'engagement',
    event_label: platform,
  });
};

/** User submits the contact form */
const trackContactSubmit = () => {
  trackEvent('contact_form_submit', {
    event_category: 'conversion',
    event_label: 'contact_page',
  });
};

/** User interacts with the map */
const trackMapInteraction = () => {
  trackEvent('map_interaction', {
    event_category: 'engagement',
    event_label: 'google_map',
  });
};

/** User scrolls to a key section */
const trackSectionView = (sectionName) => {
  trackEvent('section_view', {
    event_category: 'engagement',
    event_label: sectionName,
  });
};

/* ──────────── Clarity identity (optional) ──────────── */

/**
 * Identify a user in Clarity (e.g. after a form submission with email).
 * This is optional — call only when you have a user identifier.
 */
const identifyUser = (userId, sessionProps = {}) => {
  clarity('identify', userId, sessionProps);
};

/* ──────────── Export ──────────── */

const analytics = {
  trackPageView,
  trackEvent,
  trackPhoneClick,
  trackReservationClick,
  trackMenuView,
  trackSocialClick,
  trackContactSubmit,
  trackMapInteraction,
  trackSectionView,
  identifyUser,
};

export default analytics;
